import { useEffect, useMemo, useRef, useState } from "react"
import styled, { css, keyframes } from "styled-components"

import { Button, colors, spacing } from "@minuthq/meatball-ui-react"

import { Box } from "src/ui/Graphs/GenericTooltip"
import { HeaderDateCell } from "src/ui/Scheduler/HeaderDateCell"
import { SchedulerTooltip } from "src/ui/Scheduler/SchedulerTooltip"
import {
  DEFAULT_EVENT_COLORS,
  SCHEDULER_DIMENSIONS,
  TEventWithSpanInfo,
  TOOLTIP_DEFAULTS,
  TSchedulerDividerStyle,
  TSchedulerEvent,
  TSchedulerGridOptions,
  TSchedulerHeaderOptions,
  TSchedulerProps,
} from "src/ui/Scheduler/schedulerTypes"
import {
  allocateEventLanes,
  calculateEventSpanInfo,
  calculateTooltipPosition,
  generateHeaderCells,
  getEventColors,
  getEventsForSchedulerCell,
  isDateCurrent,
} from "src/ui/Scheduler/schedulerUtils"
import { ZonedDateTime } from "src/utils/ZonedDateTime"

export function Scheduler({
  events,
  resources,
  startDate,
  endDate,
  timezone,
  onEventClick,
  renderEvent,
  renderResourceLabel,
  headerOptions,
  gridOptions,
  style,
  viewType = "week",
  maxLanes = 4,
  height,
  moreIndicatorText,
}: TSchedulerProps) {
  const visibleEvents = useMemo(() => {
    return events.filter((event) => {
      return event.start < endDate && event.end > startDate
    })
  }, [events, startDate, endDate])

  const now = ZonedDateTime.now(timezone)

  // Refs for tooltip dimensions and scrollable area
  const tooltipRef = useRef<HTMLDivElement>(null)
  const scrollableAreaRef = useRef<HTMLDivElement>(null)
  const previousViewTypeRef = useRef(viewType)
  const previousResourcesLengthRef = useRef(resources?.length ?? 0)

  // Generate header cells based on view type and date range
  const headerCells: ZonedDateTime[] = useMemo(() => {
    return generateHeaderCells({
      viewType,
      startDate,
      endDate,
    })
  }, [viewType, startDate, endDate])

  // Find current period index and scroll to it when viewType changes
  const currentPeriodIndex = useMemo(() => {
    return headerCells.findIndex((date) =>
      isDateCurrent({
        date,
        now,
        viewType,
      })
    )
  }, [headerCells, now, viewType])

  // Scroll to current period only when viewType actually changes
  if (
    currentPeriodIndex !== -1 &&
    scrollableAreaRef.current &&
    previousViewTypeRef.current !== viewType
  ) {
    requestAnimationFrame(() => {
      if (scrollableAreaRef.current) {
        const cellWidth = SCHEDULER_DIMENSIONS.MIN_WIDTH
        const stickyWidth = SCHEDULER_DIMENSIONS.STICKY_LABEL_WIDTH
        const currentPeriodLeft = stickyWidth + currentPeriodIndex * cellWidth
        const containerWidth = scrollableAreaRef.current.clientWidth
        const scrollLeft =
          currentPeriodLeft - containerWidth / 2 + cellWidth / 2
        scrollableAreaRef.current.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        })
      }
    })
    previousViewTypeRef.current = viewType
  }

  /**
   * Scrolls to the bottom of the scheduler vertically
   */
  function scrollToBottom() {
    if (!scrollableAreaRef.current) {
      return
    }

    requestAnimationFrame(() => {
      if (scrollableAreaRef.current) {
        const scrollHeight = scrollableAreaRef.current.scrollHeight
        const clientHeight = scrollableAreaRef.current.clientHeight
        const maxScrollTop = scrollHeight - clientHeight

        scrollableAreaRef.current.scrollTo({
          top: Math.max(0, maxScrollTop),
          behavior: "smooth",
        })
      }
    })
  }

  // Auto-scroll to bottom when new resources are added
  useEffect(() => {
    const currentResourcesLength = resources?.length ?? 0
    if (
      currentResourcesLength > previousResourcesLengthRef.current &&
      previousResourcesLengthRef.current > 0
    ) {
      scrollToBottom()
    }
    previousResourcesLengthRef.current = currentResourcesLength
  }, [resources?.length])

  /**
   * Filters events that intersect with a specific cell (day/month) for a given resource
   * @param resourceId - The resource ID to filter by
   * @param cellDate - The date of the cell to check
   * @returns Array of events that overlap with the cell
   */
  function getEventsForCellLocal(resourceId: string, cellDate: ZonedDateTime) {
    return getEventsForSchedulerCell({
      events: visibleEvents,
      resourceId,
      cellDate,
      viewType,
    })
  }

  // Get the span width for an event - calculate based on actual days occupied
  // Events are placed based on their actual start and end times, without special handling for midnight
  function getEventSpanInfoLocal(event: TSchedulerEvent, columnIndex: number) {
    return calculateEventSpanInfo({
      event,
      columnIndex,
      headerCells,
      viewType,
    })
  }

  const [tooltip, setTooltip] = useState<{
    content: React.ReactNode
    x: number
    y: number
    position: {
      left: number
      top: number
      transform: string
    }
  } | null>(null)

  // Lane allocation algorithm for non-overlapping event layout considering spanning events
  // Strategy: For each event, find the lowest available lane where it doesn't conflict
  const allocateEventLanesLocal = (
    events: Array<{
      event: TSchedulerEvent
      spanInfo: ReturnType<typeof getEventSpanInfoLocal>
    }>
  ): Array<{
    event: TSchedulerEvent
    spanInfo: ReturnType<typeof getEventSpanInfoLocal>
    lane: number
  }> => {
    return allocateEventLanes({
      events: events as TEventWithSpanInfo[],
      viewType,
    })
  }

  // Full width = resource column + timeline grid
  const gridWidth =
    SCHEDULER_DIMENSIONS.STICKY_LABEL_WIDTH +
    headerCells.length * SCHEDULER_DIMENSIONS.MIN_WIDTH

  /**
   * Extracts and resolves event colors with proper fallbacks
   * @param event - The scheduler event
   * @returns Object with backgroundColor and textColor
   */
  function getEventColorsLocal(event: TSchedulerEvent) {
    return getEventColors({
      event,
      defaultColors: {
        backgroundColor: DEFAULT_EVENT_COLORS.BACKGROUND,
        textColor: DEFAULT_EVENT_COLORS.TEXT,
      },
    })
  }

  /**
   * Creates standardized tooltip event handlers for events
   * @param events - Array of events to display in the tooltip
   * @returns Object with onMouseEnter, onMouseMove, and onMouseLeave handlers
   */
  function createTooltipHandlers(events: TSchedulerEvent[]) {
    const getMousePosition = (
      e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
    ) => ({
      x: e.clientX,
      y: e.clientY + TOOLTIP_DEFAULTS.Y_OFFSET,
    })

    return {
      onMouseEnter: (
        e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
      ) => {
        const { x: mouseX, y: mouseY } = getMousePosition(e)

        setTooltip({
          content: <SchedulerTooltip events={events} />,
          x: mouseX,
          y: mouseY,
          position: calculateTooltipPosition({
            mouseX,
            mouseY,
          }),
        })
      },
      onMouseMove: (
        e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
      ) => {
        if (tooltip && tooltipRef.current) {
          const rect = tooltipRef.current.getBoundingClientRect()
          const { x: mouseX, y: mouseY } = getMousePosition(e)

          setTooltip({
            ...tooltip,
            x: mouseX,
            y: mouseY,
            position: calculateTooltipPosition({
              mouseX,
              mouseY,
              tooltipWidth: rect.width,
              tooltipHeight: rect.height,
            }),
          })
        }
      },
      onMouseLeave: () => setTooltip(null),
    }
  }

  return (
    <ScheduleWrapper style={style} $height={height}>
      <ScrollableGridArea ref={scrollableAreaRef} data-scheduler-scrollable>
        <GridContentWrapper $gridWidth={gridWidth}>
          {/* Header as first row - Make sticky */}
          <ResourceRowContainer
            $gridOptions={gridOptions}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: colors.textContrast,
            }}
          >
            <StickyResourceLabel $isHeader $headerOptions={headerOptions}>
              {headerOptions?.resourceTitle || ""}
            </StickyResourceLabel>
            {headerCells.map((date, i) => (
              <Cell key={i} $isHeader $headerOptions={headerOptions}>
                <HeaderDateCell
                  date={date}
                  viewType={viewType}
                  isCurrentPeriod={isDateCurrent({
                    date,
                    now,
                    viewType,
                  })}
                  onDateClick={headerOptions?.onDateClick}
                  {...headerOptions?.headerDateStyles}
                />
              </Cell>
            ))}
          </ResourceRowContainer>
          {/* Resource rows */}
          {(resources ?? []).map((resource) => {
            // Pre-allocate lanes for all events of this resource to ensure consistency across days
            const resourceEvents = visibleEvents.filter(
              (e) => e.resourceId === resource.id
            )

            // Group events by their span info and allocate global lanes
            const eventsWithSpanInfo = resourceEvents.map((event) => ({
              event,
              spanInfo: getEventSpanInfoLocal(event, 0),
            }))
            // Allocate global lanes for this resource
            const globalEventLanes = allocateEventLanesLocal(eventsWithSpanInfo)
            return (
              <ResourceRowContainer
                key={resource.id}
                $gridOptions={gridOptions}
              >
                <StickyResourceLabel $headerOptions={headerOptions}>
                  {renderResourceLabel
                    ? renderResourceLabel(resource)
                    : resource.name}
                </StickyResourceLabel>
                {headerCells.map((date, i) => {
                  const cellEvents = getEventsForCellLocal(resource.id, date)
                  // Filter events that start in this cell and get their lane assignments
                  const eventsToRender: Array<{
                    event: TSchedulerEvent
                    spanInfo: ReturnType<typeof getEventSpanInfoLocal>
                    lane: number
                  }> = []

                  cellEvents.forEach((event) => {
                    const spanInfo = getEventSpanInfoLocal(event, i)
                    if (spanInfo.isSpanStart) {
                      const laneAssignment = globalEventLanes.find(
                        (laneInfo) => laneInfo.event.id === event.id
                      )
                      if (laneAssignment) {
                        eventsToRender.push({
                          event,
                          spanInfo,
                          lane: laneAssignment.lane,
                        })
                      }
                    }
                  })
                  const sortedEvents = eventsToRender.sort(
                    (a, b) => a.lane - b.lane
                  )
                  const visibleEvents = sortedEvents.filter(
                    (event) => event.lane < maxLanes
                  )
                  const hiddenEvents = sortedEvents.filter(
                    (event) => event.lane >= maxLanes
                  )
                  const hiddenEventsForTooltip = hiddenEvents.map(
                    (event) => event.event
                  )
                  const hiddenEventCount = hiddenEvents.length

                  // Calculate cell height based on visible events and lane requirements
                  const maxVisibleLane =
                    visibleEvents.length > 0
                      ? Math.max(...visibleEvents.map((event) => event.lane))
                      : -1
                  const totalLanesNeeded =
                    maxVisibleLane + 1 + (hiddenEventCount > 0 ? 1 : 0)
                  const cellHeight = Math.max(
                    totalLanesNeeded * SCHEDULER_DIMENSIONS.LANE_HEIGHT +
                      SCHEDULER_DIMENSIONS.LANE_TOP_MARGIN +
                      SCHEDULER_DIMENSIONS.CELL_BOTTOM_PADDING,
                    SCHEDULER_DIMENSIONS.MIN_CELL_HEIGHT
                  )
                  return (
                    <Cell
                      key={i}
                      $gridOptions={gridOptions}
                      $isCurrentPeriod={
                        isDateCurrent({
                          date,
                          now,
                          viewType,
                        }) && !!gridOptions?.highlightCurrentPeriod
                      }
                      $highlightColor={
                        gridOptions?.highlightCurrentPeriod?.backgroundColor
                      }
                      style={{ minHeight: `${cellHeight}px` }}
                    >
                      {visibleEvents.map(({ event, spanInfo, lane }) => {
                        // Lane information is available here for vertical positioning

                        const tooltipHandlers = createTooltipHandlers([event])

                        const eventColors = getEventColorsLocal(event)

                        return (
                          <SpanningEventBlock
                            key={event.id}
                            $spanWidth={spanInfo.spanWidth}
                            $lane={lane}
                            $backgroundColor={eventColors.backgroundColor}
                            $textColor={eventColors.textColor}
                            $borderColor={eventColors.borderColor}
                            onClick={() => onEventClick?.(event)}
                            {...tooltipHandlers}
                          >
                            {renderEvent ? renderEvent(event) : event.title}
                          </SpanningEventBlock>
                        )
                      })}
                      {hiddenEventCount > 0 && (
                        <MoreIndicatorButton
                          size="small"
                          variant="minimal"
                          {...createTooltipHandlers(hiddenEventsForTooltip)}
                        >
                          {moreIndicatorText
                            ? moreIndicatorText(hiddenEventCount)
                            : `+${hiddenEventCount} more`}
                        </MoreIndicatorButton>
                      )}
                    </Cell>
                  )
                })}
              </ResourceRowContainer>
            )
          })}
        </GridContentWrapper>
      </ScrollableGridArea>
      {/* Tooltip */}
      {tooltip && (
        <AnimatedTooltipBox
          ref={tooltipRef}
          $fade={tooltip ? "in" : "out"}
          style={{
            left: tooltip?.position.left,
            top: tooltip?.position.top,
            transform: tooltip?.position.transform,
            position: "fixed",
            pointerEvents: "none",
            background: colors.textContrast,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: 8,
            zIndex: 1000,
          }}
        >
          {tooltip?.content}
        </AnimatedTooltipBox>
      )}
    </ScheduleWrapper>
  )
}

function getBorderStyle(options?: TSchedulerDividerStyle): string {
  if (!options?.show) {
    return "none"
  }
  return `${options.width || 1}px solid ${options.color || colors.divider}`
}

function getHeaderBorderStyle(
  isHeader: boolean,
  headerOptions?: { columnDividers?: TSchedulerDividerStyle }
): string {
  if (!isHeader) {
    return `1px solid ${colors.divider}`
  }
  if (!headerOptions?.columnDividers?.show) {
    return "none"
  }
  return getBorderStyle(headerOptions.columnDividers)
}

const headerCellMixin = css`
  min-height: ${parseInt(spacing.L, 10) * 2}px;
  padding: 0;
  text-align: center;
  z-index: 10;
`

const dataCellMixin = css`
  min-height: ${spacing.XS};
  padding: 0;
  text-align: left;
`

const currentDayMixin = (highlightColor?: string) => css`
  background: ${highlightColor || colors.accentSecondaryLight};
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(8px);
  }
`

const AnimatedTooltipBox = styled(Box)<{ $fade: "in" | "out" }>`
  ${({ $fade }) =>
    $fade === "in"
      ? css`
          animation: ${fadeIn} 0.18s cubic-bezier(0.39, 0.575, 0.565, 1) both;
        `
      : css`
          animation: ${fadeOut} 0.18s both;
        `}
`

const ScheduleWrapper = styled.div<{ $height?: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: auto;
  max-height: ${({ $height }) => ($height ? `${$height}px` : "none")};
  background: #fff;
  font-family: "Figtree", sans-serif;
  overflow: hidden;
  position: relative;
`

const Cell = styled.div<{
  $gridOptions?: TSchedulerGridOptions
  $isCurrentPeriod?: boolean
  $highlightColor?: string
  $isHeader?: boolean
  $headerOptions?: TSchedulerHeaderOptions
}>`
  display: block;
  min-width: ${SCHEDULER_DIMENSIONS.MIN_WIDTH}px;
  flex: 1 1 ${SCHEDULER_DIMENSIONS.MIN_WIDTH}px;
  position: relative;
  padding-bottom: ${SCHEDULER_DIMENSIONS.CELL_BOTTOM_PADDING}px;
  color: ${colors.textSecondary};
  box-sizing: border-box;
  border-bottom: none;
  border-right: ${({ $gridOptions, $isHeader, $headerOptions }) =>
    $isHeader
      ? getHeaderBorderStyle($isHeader, $headerOptions)
      : getBorderStyle($gridOptions?.verticalLines)};

  /* Apply header or data cell styling */
  ${({ $isHeader }) => ($isHeader ? headerCellMixin : dataCellMixin)}

  /* Apply current day highlighting */
  ${({ $isCurrentPeriod, $highlightColor }) =>
    $isCurrentPeriod && currentDayMixin($highlightColor)}
`

const MoreIndicatorButton = styled(Button)`
  position: absolute;
  right: 4px;
  bottom: 10px;
  z-index: 3;
`

const ResourceRowContainer = styled.div<{
  $gridOptions?: TSchedulerGridOptions
}>`
  display: flex;
  width: 100%;
  align-items: stretch;
  position: sticky;
  background: transparent;
  min-height: fit-content;
  border-bottom: ${({ $gridOptions }) =>
    $gridOptions?.horizontalLines?.show
      ? `${$gridOptions?.horizontalLines?.width || 1}px solid ${
          $gridOptions?.horizontalLines?.color || colors.divider
        }`
      : "none"};
`

const StickyResourceLabel = styled.div<{
  $isHeader?: boolean
  $headerOptions?: TSchedulerHeaderOptions
}>`
  position: sticky;
  left: 0;
  z-index: ${({ $isHeader }) => ($isHeader ? 40 : 30)};
  background: ${colors.textContrast};
  width: ${SCHEDULER_DIMENSIONS.STICKY_LABEL_WIDTH}px;
  padding: ${spacing.S};
  font-size: 14px;
  font-weight: 600;
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border-left: 1px solid ${colors.divider};
  border-right: 1px solid ${colors.divider};
  ${({ $isHeader }) =>
    $isHeader &&
    css`
      border: none;
      align-items: end;

      /* Create a clip-path that allows the right border of adjacent cells to show through */
      clip-path: polygon(
        0 0,
        calc(100% - 1px) 0,
        calc(100% - 1px) 100%,
        0 100%
      );
    `}
`

const GridContentWrapper = styled.div<{ $gridWidth: number }>`
  display: flex;
  flex-direction: column;
  width: ${({ $gridWidth }) => `max(100%, ${$gridWidth}px)`};
`

const ScrollableGridArea = styled.div`
  overflow-x: auto;
  width: 100%;
  background: transparent;
`

const SpanningEventBlock = styled.div<{
  $spanWidth: number
  $lane?: number
  $backgroundColor?: string
  $textColor?: string
  $borderColor?: string
}>`
  position: absolute;
  top: ${({ $lane = 0 }) =>
    SCHEDULER_DIMENSIONS.LANE_TOP_MARGIN +
    $lane * SCHEDULER_DIMENSIONS.LANE_HEIGHT}px;
  left: ${SCHEDULER_DIMENSIONS.EVENT_LEFT_MARGIN}px;
  z-index: 2;
  display: block;
  width: ${({ $spanWidth }) =>
    `calc(${$spanWidth * 100}% - ${SCHEDULER_DIMENSIONS.EVENT_WIDTH_MARGIN}px)`};
  height: ${SCHEDULER_DIMENSIONS.EVENT_HEIGHT}px;
  min-width: 60px;
  padding: ${SCHEDULER_DIMENSIONS.EVENT_PADDING};
  border-radius: 4px;
  font-size: 12px;
  color: ${({ $textColor }) => $textColor ?? DEFAULT_EVENT_COLORS.TEXT};
  background: ${({ $backgroundColor }) =>
    $backgroundColor ?? DEFAULT_EVENT_COLORS.BACKGROUND};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: ${({ $borderColor }) =>
    $borderColor ? `1px solid ${$borderColor}` : "none"};
`
